/* eslint-disable max-statements */
import { Button, Group, LoadingOverlay, Stack, Text } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { useAdminImportTemplate, useDeclarants } from '@admin/hooks';
import { MultiSelect, UploadFile } from '@components';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';

interface ImportTemplateProps {
  onClose: () => void;
}

const importTemplateSchema = z.object({
  declarants: z.array(z.string()).min(1, { message: 'Required' }),
  file: z.instanceof(File),
});

type ImportTemplateValues = z.infer<typeof importTemplateSchema>;

export const ImportTemplate: React.FC<ImportTemplateProps> = ({ onClose }) => {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const importTemplateMutation = useAdminImportTemplate();

  const { data: declarantsList, isLoading: declarantsLoading } = useDeclarants();

  useEffect(() => {
    if (!declarantsList.length) {
      setErrorMessage('No declarants available.');
    } else {
      setErrorMessage(null);
    }
  }, [declarantsList]);

  const form = useFormWithNameInputProp<ImportTemplateValues>({
    initialValues: {
      declarants: [],
      file: null,
    },
    validate: zodResolver(importTemplateSchema),
    validateInputOnBlur: true,
  });

  const declarantsSelectData =
    declarantsList?.map((declarant) => ({
      value: declarant._id,
      label: declarant.accountName,
    })) || [];

  const selectedDeclarantValues = (form.values.declarants || []).map((id: string) => {
    const found = declarantsSelectData.find((item) => item.value === id);
    return found ? found.label : id;
  });

  const handleDeclarantsChange = (selectedIds: string[]) => {
    form.setFieldValue('declarants', selectedIds);
  };

  const onSubmit = async (values: ImportTemplateValues) => {
    setErrorMessage(null);
    setSubmitting(true);

    try {
      await importTemplateMutation.mutateAsync(
        {
          declarantIds: values.declarants,
          file: values.file,
        },
        {
          onSuccess: () => {
            onClose();
          },
        },
      );
    } catch (error) {
      setErrorMessage((error as any).response?.data?.message ?? 'An error occurred while importing the batch');
    } finally {
      setSubmitting(false);
    }
  };

  if (declarantsLoading) {
    return <LoadingOverlay visible overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm" mb="xl">
        <MultiSelect
          label="Declarants"
          data={declarantsSelectData}
          value={selectedDeclarantValues}
          onChange={handleDeclarantsChange}
          fetchOptions={async () => declarantsSelectData}
          miw={600}
          w="70%"
        />
        <UploadFile {...form.getInputProps('file')} isMulti={false} type="zip" p="xs" />

        {errorMessage && (
          <Text c="red" fw="bold">
            {errorMessage}
          </Text>
        )}
      </Stack>
      <Group justify="center">
        <Button type="button" variant="transparent" onClick={onClose} disabled={submitting} size="lg">
          Cancel
        </Button>
        <Button type="submit" loading={submitting} disabled={!form.isValid()} variant="filled" size="lg">
          Import
        </Button>
      </Group>
    </form>
  );
};
